<script setup>
import { useAuth, useClerk } from '@clerk/vue'
import { isEmpty, isUndefined } from 'lodash'
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'

import WebAppAuthenticationApiHandler from '@/api/web-app-authentication'
import { handleError } from '@/utils/error-handling'
import { MCP_REDIRECT_PAYLOAD_KEY } from '@/utils/query-parameters'
import { useReloadPage } from '@/utils/reload-page'
import { normalizeOrganization } from '@/utils/web-app/organization-helpers'
import LoginAppLoader from '@web-app/login-app/LoginAppLoader'
import MCPConnector from '@web-app/login-app/MCPConnector'
import { useLoginAppStores } from '@web-app/utils/login-app-stores'

defineOptions({
  name: 'AfterLoginGateway'
})

const api = new WebAppAuthenticationApiHandler()

const store = useStore()

const appUrl = computed(() => store.state.appContext.urls.webAppUrl)

//#region STORES SCOPE

const {
  redirectParamsState,
  refererRoute,
  resolveRefererRoute,
  isMcpLoginApp,
  resetMcpRedirectParamsStore,
  revokeMcpRedirectParams,
  validateMcpRedirectParams,
  switchOrganizationState,
  validateSwitchOrganizationState,
  resetSwitchOrganizationState
} = useLoginAppStores({ watchForRefererRoute: false })

//#endregion STORES SCOPE

const { isLoaded: authProviderLoaded, getToken, isSignedIn } = useAuth()
const getAuthToken = async () => {
  const fallback = ''
  if (!authProviderLoaded.value) {
    return fallback
  }

  try {
    return await getToken.value()
  } catch (error) {
    handleError({ error })
    return fallback
  }
}

//#region MCP AUTH SCOPE

const userOrganizations = ref([])
const selectedOrganization = ref(null)
const isShowMCPConnector = ref(false)
const shouldShowMCPConnector = computed(() => {
  return isMcpLoginApp.value && isShowMCPConnector.value
})

const onConfirmConnection = id => {
  selectOrganization(id)
  connectMCP()
}

const connectMCP = async () => {
  if (selectedOrganization.value) {
    try {
      const authorizationToken = await getAuthToken()
      const { redirectUrl, token } = await api.connectMCP({
        token: authorizationToken,
        iss: selectedOrganization.value
      })

      const url = new URL(redirectUrl)
      url.searchParams.set('token', token)
      url.searchParams.set(MCP_REDIRECT_PAYLOAD_KEY, redirectParamsState.value.params)
      resetMcpRedirectParamsStore()
      window.location.replace(url.toString())
    } catch (error) {
      handleError({ error })
      revokeMcpRedirectParams()
    }
  }
}

const selectOrganization = id => {
  selectedOrganization.value = id
}

const MCPPostLogin = async () => {
  try {
    const authorizationToken = await getAuthToken()

    const { organizations = [] } = await api.MCPPostLogin({
      token: authorizationToken
    })

    userOrganizations.value = organizations.map(normalizeOrganization)

    isShowMCPConnector.value = true

    if (isEmpty(organizations)) {
      revokeMcpRedirectParams()
    }
  } catch (error) {
    handleError({ error })
    revokeMcpRedirectParams()
  }
}

//#endregion MCP AUTH SCOPE

//#region SWITCH ORGANIZATION SCOPE

const authProvider = useClerk()

const { reloadPage } = useReloadPage()

const switchOrganization = async () => {
  try {
    await authProvider.value.setActive({
      organization: switchOrganizationState.value.organizationId
    })
    resetSwitchOrganizationState()
  } catch (error) {
    resetSwitchOrganizationState()
    reloadPage()
    // Throwing an error is unnecessary, but it doesn't break anything and
    // helps prevent `postLogin` from executing if the page reload fails.
    throw error
  }
}

//#endregion SWITCH ORGANIZATION SCOPE

const runAfterLoginFlow = async () => {
  if (validateMcpRedirectParams()) {
    await MCPPostLogin()
    return
  }

  if (validateSwitchOrganizationState()) {
    await switchOrganization()
    await postLogin()
    return
  }

  resolveRefererRoute()
  await postLogin()
}

const postLogin = async () => {
  try {
    const authorizationToken = await getAuthToken()

    await api.postLogin({ token: authorizationToken })
    window.location.replace(new URL(refererRoute.value || '', appUrl.value).toString())
  } catch (error) {
    handleError({ error })
  }
}

let stop

const watchHandler = watch(
  isSignedIn,
  async (newValue, oldValue) => {
    if (!authProviderLoaded.value || isUndefined(newValue)) return
    if (newValue === oldValue) return

    if (newValue) {
      stop?.()
      await runAfterLoginFlow()
    }
  },
  { immediate: true }
)

stop = watchHandler.stop
</script>

<template>
  <MCPConnector
    v-if="shouldShowMCPConnector"
    :organizations="userOrganizations"
    :redirect-params="redirectParamsState.params"
    @confirm="onConfirmConnection"
  />

  <LoginAppLoader v-else />
</template>

<style lang="scss" scoped></style>
